import React, { useState } from "react";
import MapTo from "../../utils/mapto";
import styled from "styled-components";
import Cta from "@components/CTA";
import Modal from "@components/Modal/Modal";
import GTM from "@utils/GTM";

import watchVideoIcon from "@images/icons/watch.svg";

const StyledContainer = styled.div`
    background: ${props => props.backgroundColor || '#F5F5F5'};
    box-shadow: ${props => props.backgroundShadow || '0px 0px 10px 0px rgba(0, 0, 0, 0.05) inset, 0px 13px 17px -10px rgba(61, 78, 83, 0.10)'};
    border: 1px solid var(--Glass-Stroke-Light, #FFF);
    backdrop-filter: blur(50px);

    .default-card__content{
        p:last-child{
            margin-top: 15px;
            color: #000000B2
        }
        p:first-child{
            &:last-child{
                font-size: 16px;
                margin-top: 0;
                color: #165F57;
            }
        }
    }
`;

// VideoModal component to display videos in a modal
const VideoModal = ({ showModal, closeModal, videoId, videoHash, videoTitle }) => {

    // Ensure videoId is properly formatted for embedding
    const getVideoSrc = (id, hash) => {
        // If it's already an iframe src URL, use it directly
        if (!id || id === "" || id === null) {
            return null;
        }

        return `https://player.vimeo.com/video/${id}?h=${hash}&title=0&byline=0&portrait=0&badge=0&autopause=0&autoplay=1`;
    };

    const videoSrc = getVideoSrc(videoId, videoHash);
    const displayTitle = videoTitle || "Video";

    // Track escape key separately using an effect
    React.useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                GTM.trackCloseModal('Escape', displayTitle);
            }
        };

        const handleOutsideClick = (e) => {
            if (!e.target.closest('.cross-button')) {
                GTM.trackCloseModal('Outside', displayTitle);
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscKey);
            document.addEventListener('mousedown', handleOutsideClick);
            return () => {
                document.removeEventListener('keydown', handleEscKey);
                document.removeEventListener('mousedown', handleOutsideClick);
            };
        }
    }, [showModal, displayTitle]);

    return (
        <Modal
            showModal={showModal}
            closeModal={closeModal}
            dialogPanelClassName="max-w-[1150px] h-auto rounded-all_48 p-6 md:p-8 !max-w-6xl w-full"
        >
            <div className="relative w-full h-full flex flex-col">
                <button
                    onClick={() => {
                        closeModal();
                        GTM.trackCloseModal('X', displayTitle);
                    }}
                    className="absolute top-[-8px] right-[-5px] text-[#7AC593] hover:text-black z-10 cross-button"
                    aria-label="Close modal"
                    style={{
                        backdropFilter: 'blur(15px)',
                        background: 'linear-gradient(0deg, rgba(252, 252, 252, 0.70) 0%, rgba(252, 252, 252, 0.70) 100%), rgba(255, 255, 255, 0.70)',
                        border: '1px solid rgba(255, 255, 255, 0.10)',
                        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.05) inset, 0px 13px 17px -10px rgba(61, 78, 83, 0.10)',
                        padding: '15px',
                        borderRadius: '100px'
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <span className="text-dark_green font-[700] lg:text-h2 text-h4_medium mb-4 max-w-[calc(100%-50px)] w-full block">{displayTitle}</span>
                <div className="w-full relative aspect-video">
                    <iframe
                        src={videoSrc}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        title={displayTitle}
                        className="w-full h-full absolute top-0 left-0"
                    />
                </div>
            </div>
        </Modal>
    );
};

const ResourcesCard = (props, className) => {
    const items = props?.items || props?.content?.items;
    const backgroundColor = props?.backgroundColor?.trim() || (props.content && props?.content.backgroundColor?.trim());
    const backgroundShadow = props?.backgroundShadow?.trim() || (props.content && props?.content.backgroundShadow?.trim());

    // State for the video modal
    const [videoModalState, setVideoModalState] = useState({
        isOpen: false,
        videoId: "",
        videoHash: "",
        videoTitle: ""
    });

    // Function to open the video modal
    const openVideoModal = (videoId, videoHash, videoTitle) => {
        setVideoModalState({
            isOpen: true,
            videoId,
            videoHash,
            videoTitle: videoTitle
        });
    };

    // Function to close the video modal
    const closeVideoModal = () => {
        setVideoModalState({
            ...videoModalState,
            isOpen: false
        });
    };

    // Separate media and content items
    const mediaItems = items?.filter(item => item.__typename === "ContentfulMedia") || [];
    const contentItems = items?.filter(item => item.__typename === "ContentfulRichText") || [];
    const linkItems = items?.filter(item => item.__typename === "ContentfulLink") || [];
    const videoItems = items?.filter(item => item.__typename === "ContentfulVideo") || [];

    return (
        <StyledContainer
            backgroundColor={backgroundColor}
            backgroundShadow={backgroundShadow}
            className={` relative flex items-center justify-start rounded-all_48 px-[30px] py-[24px] pl-[10px]  lg:px-[32px] lg:py-[32px] lg:pt-[15px] lg:pl-[10px] ${props?.content?.className}`}
        >
            <div className="w-full flex flex-row items-start justify-start">
                {mediaItems.length > 0 && (
                    <div className={`default-card__media flex justify-center items-center ${props?.style?.mediaContainer}`}>
                        {mediaItems.map(item => (
                            item?.asset?.file?.url?.indexOf(".pdf") > -1 || item?.asset?.file?.url?.indexOf(".zip") > -1 ? null : <MapTo key={item.id} items={item} className=" lg:max-w-max_content" />
                        ))}
                    </div>
                )}
                <div className="w-full lg:pl-[10px] pt-[10px]">
                    {contentItems.length > 0 && (
                        <div className={`default-card__content flex flex-col lg:min-h-[50px] ${props?.style?.contentContainer}`}>
                            {contentItems.map(item => (
                                <MapTo key={item.id} items={item} className="[&>p:first-child]:!text-dark_green [&>p:last-child]:!text-paragraph_16 [&>p]:!mb-[8px] " />
                            ))}
                        </div>
                    )}
                    {contentItems.length > 0 && (
                        <div className={`w-full default-card__links flex flex-row [&>*]:!text-dark_green mt-[15px] h-full ${props?.style?.linkContainer} ${linkItems.length > 1 ? 'gap-0' : ''}`}>
                            {linkItems.map((item, index) => (
                                <>
                                    <MapTo key={item.id} items={item} className="!text-dark_green !font-[700]" />
                                    {index < linkItems.length - 1 && (
                                        <div className="w-[2px] bg-[#F49F65B2] m-3 mr-4">&nbsp;</div>
                                    )}
                                </>
                            ))}

                            {mediaItems.map(item => (
                                item?.asset?.file?.url?.indexOf(".pdf") > -1 || item?.asset?.file?.url?.indexOf(".zip") > -1 ? <Cta
                                    className="!text-dark_green"
                                    label="Download"
                                    displayType="Resource(Download)"
                                    link={item?.asset?.file?.url}
                                    href={item?.asset?.file?.url}
                                    target="_blank"
                                /> : null
                            ))}

                            {videoItems.map(item => (
                                <Cta
                                    key={item.id}
                                    className="!text-dark_green"
                                    label={item?.buttonLabel}
                                    displayType="Resource(Watch-Video)"
                                    onClick={() => {
                                        openVideoModal(item?.videoId, item?.videoHash, item?.videoTitle);
                                        GTM.videoStart(item?.videoTitle);
                                        GTM.trackOpenModal(item?.videoTitle);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Video Modal */}
            <VideoModal
                showModal={videoModalState.isOpen}
                closeModal={closeVideoModal}
                videoId={videoModalState.videoId}
                videoHash={videoModalState.videoHash}
                videoTitle={videoModalState.videoTitle}
            />
        </StyledContainer>
    );
};

export default ResourcesCard; 